import React, { useEffect, useState } from 'react';
import { AlertTriangle, AlertCircle, Info, Check, X, RefreshCw, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  listTarefas,
  getTarefasStats,
  refreshTarefasComputed,
  dismissTarefa,
  Tarefa,
  TarefasStats,
} from '../services/apiService';
import { MarkDoneModal } from './MarkDoneModal';

const SEVERITY_STYLES: Record<string, { icon: React.ReactNode; bg: string; text: string }> = {
  critical: {
    icon: <AlertCircle className="h-4 w-4" />,
    bg: 'bg-red-50 border-red-200',
    text: 'text-red-800',
  },
  warning: {
    icon: <AlertTriangle className="h-4 w-4" />,
    bg: 'bg-amber-50 border-amber-200',
    text: 'text-amber-800',
  },
  info: {
    icon: <Info className="h-4 w-4" />,
    bg: 'bg-blue-50 border-blue-200',
    text: 'text-blue-800',
  },
};

function fmtDate(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}`;
}

function daysOverdue(due: string): number {
  if (!due) return 0;
  const today = new Date();
  const dueDate = new Date(due + 'T00:00:00');
  return Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
}

export const TarefasWidget: React.FC = () => {
  const [tarefas, setTarefas] = useState<Tarefa[]>([]);
  const [stats, setStats] = useState<TarefasStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [doneModalTarefa, setDoneModalTarefa] = useState<Tarefa | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        listTarefas('open'),
        getTarefasStats(),
      ]);
      setTarefas(list.tarefas.slice(0, 5));
      setStats(st);
      setError('');
    } catch {
      setError('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshTarefasComputed();
      await reload();
    } catch {
      setError('Erro ao atualizar');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDone = (tarefa: Tarefa) => {
    setDoneModalTarefa(tarefa);
  };

  const handleDismiss = async (tarefaId: string) => {
    if (!window.confirm('Descartar tarefa?')) return;
    try {
      await dismissTarefa(tarefaId);
      await reload();
    } catch {
      setError('Erro ao descartar');
    }
  };

  useEffect(() => {
    reload();
  }, []);

  if (loading && tarefas.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <span className="text-sm text-gray-500">Carregando tarefas...</span>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            Tarefas Pendentes
          </h3>
          {stats && stats.abertas > 0 && (
            <div className="flex items-center gap-1 text-xs">
              {stats.by_severity?.critical > 0 && (
                <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                  {stats.by_severity.critical} crítica{stats.by_severity.critical > 1 ? 's' : ''}
                </span>
              )}
              {stats.overdue > 0 && (
                <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                  {stats.overdue} vencida{stats.overdue > 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
          title="Atualizar (roda checks auto-computed)"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Atualizando...' : 'Atualizar'}
        </button>
      </div>

      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded">
          {error}
        </div>
      )}

      {tarefas.length === 0 ? (
        <div className="text-center py-4 text-sm text-gray-500">
          <Check className="h-6 w-6 mx-auto mb-1 text-green-500" />
          Nada pendente. Tudo em dia!
        </div>
      ) : (
        <ul className="space-y-2">
          {tarefas.map((t) => {
            const sev = t.tags.severity || 'info';
            const style = SEVERITY_STYLES[sev] || SEVERITY_STYLES.info;
            const due = t.tags['due-date'];
            const overdue = due && daysOverdue(due) > 0;
            const actionUrl = t.tags['action-url'];

            return (
              <li
                key={t['tarefa-id']}
                className={`border rounded p-2 text-sm ${style.bg}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className={`flex items-start gap-2 ${style.text} flex-1 min-w-0`}>
                    <span className="mt-0.5">{style.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{t.title}</div>
                      {t.tags.description && (
                        <div className="text-xs mt-1 whitespace-pre-line opacity-90">
                          {t.tags.description.replace(/\\n/g, '\n')}
                        </div>
                      )}
                      {due && (
                        <div className="text-xs mt-0.5 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {overdue ? (
                            <span className="font-medium">
                              Vencido {daysOverdue(due)}d (era {fmtDate(due)})
                            </span>
                          ) : (
                            <span>Vence {fmtDate(due)}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {actionUrl && (
                      <Link
                        to={actionUrl}
                        className="text-xs px-2 py-1 bg-white text-gray-700 rounded hover:bg-gray-50 border border-gray-200"
                        title="Ir para ação"
                      >
                        Abrir
                      </Link>
                    )}
                    <button
                      onClick={() => handleDone(t)}
                      className="p-1 text-green-600 hover:bg-green-100 rounded"
                      title="Marcar concluída"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDismiss(t['tarefa-id'])}
                      className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                      title="Descartar"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {stats && stats.abertas > 5 && (
        <div className="text-xs text-gray-500 text-center">
          + {stats.abertas - 5} outras tarefas
        </div>
      )}

      {doneModalTarefa && (
        <MarkDoneModal
          tarefa={doneModalTarefa}
          onClose={() => setDoneModalTarefa(null)}
          onDone={() => {
            setDoneModalTarefa(null);
            reload();
          }}
        />
      )}
    </div>
  );
};
